import { TARGET_SEND_PARAMS } from 'src/domain/sendParam'
import {
  SalesReportWithTenant,
  SalesReportRegister,
  SalesReportCalculationResult,
  TenantResentRegister,
} from 'src/slices/services/api'
import { safeParseNumber } from 'src/utils/number'

const SALES_DATA_TYPES = {
  NET_SALES: {
    category: 'net_sales',
    displayName: '純売上',
  },
  OTHER_DEDUCTIONS: {
    category: 'other',
    displayName: 'その他控除',
  },
} as const

// sendParameterName定数定義（組織別）
const SEND_PARAMETER_CODES = {
  // 組織コード 007/988 用パラメータ
  NET_SALES_007_988: '200',
  OTHER_DEDUCTIONS_007_988: '107',
  // 組織コード 999 用パラメータ
  NET_SALES_999: 'netSales',
} as const

const buildTenantMessage = (
  reportMessage: string | undefined,
  resentRegisters: TenantResentRegister[] | undefined
): string | undefined => {
  const messages = [
    reportMessage ?? '',
    ...(resentRegisters?.map((r) => r.resentMessage ?? '') || []),
  ]

  if (messages.every((m) => m === '')) {
    return undefined
  }

  return messages.join(' / ')
}

export interface ProcessingStats {
  totalTenants: number
  successfullyProcessed: number
  partiallyProcessed: number
  failed: number
}

export interface ProcessingResult extends VerificationSheetData {
  stats: ProcessingStats
}

/**
 * 検証シートデータ（集計値と詳細行を含む）
 */
export interface VerificationSheetData {
  tenantStats: {
    reportedTenantCount: number
    requiredToReportCount: number
  }
  totals: {
    netSales: number // 純売上の確定値合計
    otherDeductions: number // その他控除の確定値合計
  }
  detailRows: VerificationSheetDetailRow[]
}

/**
 * 値の種類を表す型
 */
type ValueData = {
  ocrValue: number
  manualInputValue: number
  definiteValue: number
}

/**
 * カテゴリ別の値データ（純売上、その他控除）
 */
type CategoryValues = {
  netSales: ValueData
  otherDeductions: ValueData
}

/**
 * 検証シート詳細行（1テナント = 2行：純売上 + その他控除）
 */
export interface VerificationSheetDetailRow {
  salesDate: string // 売上日
  storeCode: string // 店舗コード
  storeName: string // 店舗名
  tenantCode: string // テナントコード
  tenantName: string // テナント名
  item:
    | typeof SALES_DATA_TYPES.NET_SALES.displayName
    | typeof SALES_DATA_TYPES.OTHER_DEDUCTIONS.displayName // 項目
  ocrValue: number // OCR値
  manualInputValue: number // 手入力値
  definiteValue: number // 確定値
  tenantMessage?: string // テナントからの補足事項
  adminMemo?: string // 管理者メモ（報告ごと）
}

/**
 * テナント処理用データ構造
 */
type TenantProcessingData = CategoryValues & {
  salesDate: string
  storeCode: string
  storeName: string
  tenantCode: string
  tenantName: string
  tenantMessage?: string
  adminMemo?: string
}

const SEND_PARAMETER_CATEGORY_MAP = {
  // 純売上カテゴリ
  [SEND_PARAMETER_CODES.NET_SALES_007_988]: SALES_DATA_TYPES.NET_SALES.category,
  [SEND_PARAMETER_CODES.NET_SALES_999]: SALES_DATA_TYPES.NET_SALES.category,

  // その他控除カテゴリ
  [SEND_PARAMETER_CODES.OTHER_DEDUCTIONS_007_988]:
    SALES_DATA_TYPES.OTHER_DEDUCTIONS.category,
} as const

const isSendParameterCode = (
  value: string
): value is keyof typeof SEND_PARAMETER_CATEGORY_MAP => {
  return value in SEND_PARAMETER_CATEGORY_MAP
}

/**
 * sendParameterNameからカテゴリ名を取得
 */
const getCategoryFromSendParameterName = (
  sendParameterName: string
): string | null => {
  if (isSendParameterCode(sendParameterName)) {
    return SEND_PARAMETER_CATEGORY_MAP[sendParameterName]
  }
  return null
}

/**
 * 計算結果を処理して集計値を返す
 */
const processCalculationResult = (
  calcResult: SalesReportCalculationResult
): CategoryValues => {
  const result = {
    netSales: { ocrValue: 0, manualInputValue: 0, definiteValue: 0 },
    otherDeductions: { ocrValue: 0, manualInputValue: 0, definiteValue: 0 },
  }

  const ocrValue = safeParseNumber(calcResult.results.ocrValue)
  const manualInputValue = safeParseNumber(calcResult.results.manualInputValue)
  const definiteValue = safeParseNumber(calcResult.results.definiteValue)

  const categoryName = getCategoryFromSendParameterName(
    calcResult.calculationItem.sendParameterName
  )

  // カテゴリに基づいて値を設定（不明なパラメータは無視）
  switch (categoryName) {
    case SALES_DATA_TYPES.NET_SALES.category:
      result.netSales = { ocrValue, manualInputValue, definiteValue }
      break
    case SALES_DATA_TYPES.OTHER_DEDUCTIONS.category:
      result.otherDeductions = { ocrValue, manualInputValue, definiteValue }
      break
    default:
      break
  }

  return result
}

/**
 * テナント用の詳細行を生成（2行：その他控除 + 純売上）
 */
const createTenantDetailRows = (
  tenantSummary: TenantProcessingData
): VerificationSheetDetailRow[] => {
  return [
    {
      salesDate: tenantSummary.salesDate,
      storeCode: tenantSummary.storeCode,
      storeName: tenantSummary.storeName,
      tenantCode: tenantSummary.tenantCode,
      tenantName: tenantSummary.tenantName,
      item: SALES_DATA_TYPES.OTHER_DEDUCTIONS.displayName,
      ocrValue: tenantSummary.otherDeductions.ocrValue,
      manualInputValue: tenantSummary.otherDeductions.manualInputValue,
      definiteValue: tenantSummary.otherDeductions.definiteValue,
      tenantMessage: tenantSummary.tenantMessage,
      adminMemo: tenantSummary.adminMemo,
    },
    {
      salesDate: tenantSummary.salesDate,
      storeCode: tenantSummary.storeCode,
      storeName: tenantSummary.storeName,
      tenantCode: tenantSummary.tenantCode,
      tenantName: tenantSummary.tenantName,
      item: SALES_DATA_TYPES.NET_SALES.displayName,
      ocrValue: tenantSummary.netSales.ocrValue,
      manualInputValue: tenantSummary.netSales.manualInputValue,
      definiteValue: tenantSummary.netSales.definiteValue,
      tenantMessage: tenantSummary.tenantMessage,
      adminMemo: tenantSummary.adminMemo,
    },
  ]
}

/**
 * テナントの売上報告データを処理
 */
const processTenant = (
  salesReport: SalesReportWithTenant,
  orgCode: string,
  storeCode: string,
  storeName: string
): {
  detailRows: VerificationSheetDetailRow[]
  totals: {
    netSales: number // 純売上の確定値合計
    otherDeductions: number // その他控除の確定値合計
  }
} => {
  const tenantSummary: TenantProcessingData = {
    salesDate: salesReport.date,
    storeCode,
    storeName,
    tenantCode: salesReport.tenant?.code || '',
    tenantName: salesReport.tenant?.name || '',
    netSales: { ocrValue: 0, manualInputValue: 0, definiteValue: 0 },
    otherDeductions: { ocrValue: 0, manualInputValue: 0, definiteValue: 0 },
    tenantMessage: buildTenantMessage(
      salesReport.reportMessage,
      salesReport.resentRegisters
    ),
    adminMemo: salesReport.manageMessage || undefined,
  }

  salesReport.registers.forEach((register: SalesReportRegister) => {
    if (!register?.calculationResults) return

    register.calculationResults.forEach(
      (calcResult: SalesReportCalculationResult) => {
        // CSVダウンロード対象のsendParamに含まれない場合はスキップ
        if (
          !TARGET_SEND_PARAMS[orgCode]?.includes(
            calcResult.calculationItem.sendParameterName
          )
        ) {
          return
        }
        const calculationValues = processCalculationResult(calcResult)

        tenantSummary.netSales.ocrValue += calculationValues.netSales.ocrValue
        tenantSummary.netSales.manualInputValue +=
          calculationValues.netSales.manualInputValue
        tenantSummary.netSales.definiteValue +=
          calculationValues.netSales.definiteValue

        tenantSummary.otherDeductions.ocrValue +=
          calculationValues.otherDeductions.ocrValue
        tenantSummary.otherDeductions.manualInputValue +=
          calculationValues.otherDeductions.manualInputValue
        tenantSummary.otherDeductions.definiteValue +=
          calculationValues.otherDeductions.definiteValue
      }
    )
  })

  return {
    detailRows: createTenantDetailRows(tenantSummary),
    totals: {
      netSales: tenantSummary.netSales.definiteValue,
      otherDeductions: tenantSummary.otherDeductions.definiteValue,
    },
  }
}

/**
 * 売上データ統合処理
 */
export const processUnifiedSalesData = (
  salesReports: SalesReportWithTenant[],
  orgCode: string,
  storeCode: string,
  storeName: string,
  reportedTenantCount: number,
  requiredToReportCount: number
): ProcessingResult => {
  const stats: ProcessingStats = {
    totalTenants: 0,
    successfullyProcessed: 0,
    partiallyProcessed: 0,
    failed: 0,
  }

  const result: VerificationSheetData = {
    tenantStats: {
      reportedTenantCount,
      requiredToReportCount,
    },
    totals: {
      netSales: 0, // 純売上の確定値合計
      otherDeductions: 0, // その他控除の確定値合計
    },
    detailRows: [],
  }

  if (salesReports.length === 0) {
    return {
      ...result,
      stats,
    }
  }

  stats.totalTenants = salesReports.length

  salesReports.forEach((salesReport) => {
    const tenantResult = processTenant(
      salesReport,
      orgCode,
      storeCode,
      storeName
    )
    result.detailRows.push(...tenantResult.detailRows)

    // 合計値を集計
    result.totals.netSales += tenantResult.totals.netSales
    result.totals.otherDeductions += tenantResult.totals.otherDeductions

    stats.successfullyProcessed++
  })

  return { ...result, stats }
}
